import React, { useState, useEffect, useMemo } from 'react';
import { Routes, Route, Link, useParams, useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import clsx from 'clsx';
import PageHeader from '../../components/ui/PageHeader';
import UiCard from '../../components/ui/UiCard';
import { paginationItems, FORMA_LINK_BTN } from '../../utils/listPageUi';
import localStorageService from '../../utils/localStorageService';
import {
  PlusIcon,
  MagnifyingGlassIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  BuildingLibraryIcon,
  BuildingOfficeIcon,
  UsersIcon,
  ComputerDesktopIcon,
  CheckCircleIcon,
  XCircleIcon,
  XMarkIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';

const ClassroomDetail = ({ classroom }) => {
  if (!classroom) {
    return (
      <div className="rounded-xl border border-slate-200 bg-white shadow-soft dark:border-slate-700 dark:bg-slate-900/75 p-6">
        <p className="text-slate-600 dark:text-slate-400">Salle non trouvée</p>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-slate-200 bg-white shadow-soft dark:border-slate-700 dark:bg-slate-900/75 p-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6 gap-4">
        <div>
          <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-50 mb-1">{classroom.name || 'Nom non défini'}</h2>
          <p className="text-slate-600 dark:text-slate-400 text-sm">Code: {classroom.code || 'Code non défini'}</p>
        </div>
        <div className="flex items-center gap-2">
          <span className={`px-3 py-1.5 rounded-full text-sm ${
            classroom.isAvailable 
            ? 'bg-green-500/10 text-green-400' 
            : 'bg-red-500/10 text-red-400'
          }`}>
            {classroom.isAvailable ? 'Disponible' : 'Occupée'}
          </span>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <div className="bg-slate-100/80 dark:bg-slate-800/40 rounded-lg p-4">
          <div className="flex items-center mb-2">
            <BuildingOfficeIcon className="h-5 w-5 text-forma-600 dark:text-forma-400 mr-2" />
            <span className="text-slate-700 dark:text-slate-300 text-sm">Type</span>
          </div>
          <p className="text-slate-900 dark:text-slate-50">{classroom.type || 'Type non défini'}</p>
        </div>
        <div className="bg-slate-100/80 dark:bg-slate-800/40 rounded-lg p-4">
          <div className="flex items-center mb-2">
            <UsersIcon className="h-5 w-5 text-forma-600 dark:text-forma-400 mr-2" />
            <span className="text-slate-700 dark:text-slate-300 text-sm">Capacité</span>
          </div>
          <p className="text-slate-900 dark:text-slate-50">{classroom.capacity || '0'} étudiants</p>
        </div>
        <div className="bg-slate-100/80 dark:bg-slate-800/40 rounded-lg p-4">
          <div className="flex items-center mb-2">
            <BuildingLibraryIcon className="h-5 w-5 text-forma-600 dark:text-forma-400 mr-2" />
            <span className="text-slate-700 dark:text-slate-300 text-sm">Étage</span>
          </div>
          <p className="text-slate-900 dark:text-slate-50">{classroom.floor || 'Non spécifié'}</p>
        </div>
        <div className="bg-slate-100/80 dark:bg-slate-800/40 rounded-lg p-4">
          <div className="flex items-center mb-2">
            <ComputerDesktopIcon className="h-5 w-5 text-forma-600 dark:text-forma-400 mr-2" />
            <span className="text-slate-700 dark:text-slate-300 text-sm">Équipements</span>
          </div>
          <p className="text-slate-900 dark:text-slate-50">{Array.isArray(classroom.equipment) ? classroom.equipment.join(', ') : 'Aucun équipement'}</p>
        </div>
      </div>

      {classroom.description && (
        <div className="bg-slate-100/80 dark:bg-slate-800/40 rounded-lg p-4 mb-6">
          <h3 className="text-lg font-medium text-slate-900 dark:text-slate-50 mb-4">Description</h3>
          <p className="text-slate-700 dark:text-slate-300">{classroom.description}</p>
        </div>
      )}
      
      <div className="bg-slate-100/80 dark:bg-slate-800/40 rounded-lg p-4">
        <h3 className="text-lg font-medium text-slate-900 dark:text-slate-50 mb-4">Caractéristiques</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <div className="flex items-center">
            {classroom.hasProjector ? (
              <CheckCircleIcon className="h-5 w-5 text-green-400 mr-2" />
            ) : (
              <XCircleIcon className="h-5 w-5 text-red-400 mr-2" />
            )}
            <span className="text-slate-700 dark:text-slate-300">Projecteur</span>
          </div>
          <div className="flex items-center">
            {classroom.hasAirConditioning ? (
              <CheckCircleIcon className="h-5 w-5 text-green-400 mr-2" />
            ) : (
              <XCircleIcon className="h-5 w-5 text-red-400 mr-2" />
            )}
            <span className="text-slate-700 dark:text-slate-300">Climatisation</span>
          </div>
          <div className="flex items-center">
            {classroom.hasComputers ? (
              <CheckCircleIcon className="h-5 w-5 text-green-400 mr-2" />
            ) : (
              <XCircleIcon className="h-5 w-5 text-red-400 mr-2" />
            )}
            <span className="text-slate-700 dark:text-slate-300">Ordinateurs</span>
          </div>
          <div className="flex items-center">
            {classroom.hasWhiteboard ? (
              <CheckCircleIcon className="h-5 w-5 text-green-400 mr-2" />
            ) : (
              <XCircleIcon className="h-5 w-5 text-red-400 mr-2" />
            )}
            <span className="text-slate-700 dark:text-slate-300">Tableau blanc</span>
          </div>
        </div>
      </div>
    </div>
  );
};

const ClassroomForm = ({ isEditing = false, classroomData = null }) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: classroomData?.name || "",
    code: classroomData?.code || `S${Math.floor(Math.random() * 100).toString().padStart(2, '0')}`,
    type: classroomData?.type || "",
    capacity: classroomData?.capacity || "",
    floor: classroomData?.floor || "",
    isAvailable: classroomData?.isAvailable === undefined ? true : classroomData.isAvailable,
    equipment: classroomData?.equipment || [],
    description: classroomData?.description || "",
    hasProjector: classroomData?.hasProjector || false,
    hasAirConditioning: classroomData?.hasAirConditioning || false,
    hasComputers: classroomData?.hasComputers || false,
    hasWhiteboard: classroomData?.hasWhiteboard || false,
  });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (type === 'checkbox') {
      setFormData(prev => ({
        ...prev,
        [name]: checked
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    try {
      const dataToSave = {
        ...formData,
        equipment: typeof formData.equipment === 'string' ? 
                  formData.equipment.split(',').map(item => item.trim()).filter(Boolean) :
                  formData.equipment
      };

      if (isEditing && classroomData) {
        // Update existing classroom
        const updatedClassroom = {
          ...classroomData,
          ...dataToSave
        };
        localStorageService.updateItem('classrooms', updatedClassroom);
        console.log('Classroom updated successfully:', updatedClassroom);
      } else {
        // Add new classroom
        const newClassroom = localStorageService.addItem('classrooms', dataToSave);
        console.log('Classroom added successfully:', newClassroom);
      }
      navigate('/classrooms');
    } catch (error) {
      console.error('Error saving classroom data:', error);
      alert('Une erreur est survenue lors de l\'enregistrement des données');
    }
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white shadow-soft dark:border-slate-700 dark:bg-slate-900/75 p-6">
      <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-50 mb-6">
        {isEditing ? "Modifier la salle" : "Ajouter une salle"}
      </h2>
      
      <form className="space-y-6" onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-slate-700 dark:text-slate-300 text-sm font-medium mb-2">
              Nom de la salle
            </label>
                          <input 
              type="text" 
              name="name"
              className="w-full bg-slate-100/80 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-600 rounded-lg px-4 py-2.5 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-forma-500"
              value={formData.name}
              onChange={handleChange}
              placeholder="Salle A1, Laboratoire Informatique, etc."
              required
            />
          </div>
          
          <div>
            <label className="block text-slate-700 dark:text-slate-300 text-sm font-medium mb-2">
              Code
            </label>
                          <input 
              type="text" 
              name="code"
              className="w-full bg-slate-100/80 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-600 rounded-lg px-4 py-2.5 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-forma-500"
              value={formData.code}
              onChange={handleChange}
              placeholder="S01, LAB-INFO, etc."
              required
            />
          </div>
          
          <div>
            <label className="block text-slate-700 dark:text-slate-300 text-sm font-medium mb-2">
              Type
            </label>
                          <select 
              name="type"
              className="w-full bg-slate-100/80 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-600 rounded-lg px-4 py-2.5 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-forma-500"
              value={formData.type}
              onChange={handleChange}
              required
            >
              <option value="">Sélectionner un type</option>
              <option value="Salle de cours">Salle de cours</option>
              <option value="Laboratoire informatique">Laboratoire informatique</option>
              <option value="Salle de conférence">Salle de conférence</option>
              <option value="Atelier pratique">Atelier pratique</option>
            </select>
          </div>
          
          <div>
            <label className="block text-slate-700 dark:text-slate-300 text-sm font-medium mb-2">
              Capacité
            </label>
                          <input 
              type="number" 
              name="capacity"
              className="w-full bg-slate-100/80 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-600 rounded-lg px-4 py-2.5 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-forma-500"
              value={formData.capacity}
              onChange={handleChange}
              min="1"
              placeholder="Nombre d'étudiants"
              required
            />
          </div>
          
          <div>
            <label className="block text-slate-700 dark:text-slate-300 text-sm font-medium mb-2">
              Étage
            </label>
                          <input 
              type="text" 
              name="floor"
              className="w-full bg-slate-100/80 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-600 rounded-lg px-4 py-2.5 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-forma-500"
              value={formData.floor}
              onChange={handleChange}
              placeholder="RDC, 1er étage, etc."
            />
          </div>
          
          <div>
            <label className="block text-slate-700 dark:text-slate-300 text-sm font-medium mb-2">
              Statut
            </label>
                          <select 
              name="isAvailable"
              className="w-full bg-slate-100/80 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-600 rounded-lg px-4 py-2.5 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-forma-500"
              value={formData.isAvailable}
              onChange={handleChange}
            >
              <option value="true">Disponible</option>
              <option value="false">Occupée</option>
            </select>
          </div>
          
          <div className="md:col-span-2">
            <label className="block text-slate-700 dark:text-slate-300 text-sm font-medium mb-2">
              Équipements
            </label>
                          <input 
              type="text" 
              name="equipment"
              className="w-full bg-slate-100/80 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-600 rounded-lg px-4 py-2.5 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-forma-500"
              value={Array.isArray(formData.equipment) ? formData.equipment.join(', ') : formData.equipment}
              onChange={handleChange}
              placeholder="Projecteur, PC, Tableau, etc. (séparés par des virgules)"
            />
          </div>
          
          <div className="md:col-span-2">
            <label className="block text-slate-700 dark:text-slate-300 text-sm font-medium mb-2">
              Description
            </label>
                          <textarea 
              rows="3"
              name="description"
              className="w-full bg-slate-100/80 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-600 rounded-lg px-4 py-2.5 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-forma-500"
              value={formData.description}
              onChange={handleChange}
              placeholder="Description ou notes concernant cette salle..."
            ></textarea>
          </div>
          
          <div className="md:col-span-2">
            <h3 className="text-slate-700 dark:text-slate-300 text-sm font-medium mb-3">Caractéristiques</h3>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <label className="flex items-center space-x-2 cursor-pointer">
                <input 
                  type="checkbox" 
                  name="hasProjector"
                  className="w-4 h-4 rounded text-forma-600 focus:ring-forma-500"
                  checked={formData.hasProjector}
                  onChange={handleChange}
                />
                <span className="text-slate-700 dark:text-slate-300">Projecteur</span>
              </label>
              
              <label className="flex items-center space-x-2 cursor-pointer">
                <input 
                  type="checkbox" 
                  name="hasAirConditioning"
                  className="w-4 h-4 rounded text-forma-600 focus:ring-forma-500"
                  checked={formData.hasAirConditioning}
                  onChange={handleChange}
                />
                <span className="text-slate-700 dark:text-slate-300">Climatisation</span>
              </label>
              
              <label className="flex items-center space-x-2 cursor-pointer">
                <input 
                  type="checkbox" 
                  name="hasComputers"
                  className="w-4 h-4 rounded text-forma-600 focus:ring-forma-500"
                  checked={formData.hasComputers}
                  onChange={handleChange}
                />
                <span className="text-slate-700 dark:text-slate-300">Ordinateurs</span>
              </label>
              
              <label className="flex items-center space-x-2 cursor-pointer">
                <input 
                  type="checkbox" 
                  name="hasWhiteboard"
                  className="w-4 h-4 rounded text-forma-600 focus:ring-forma-500"
                  checked={formData.hasWhiteboard}
                  onChange={handleChange}
                />
                <span className="text-slate-700 dark:text-slate-300">Tableau blanc</span>
              </label>
            </div>
          </div>
        </div>
        
        <div className="flex justify-end space-x-4">
          <Link 
            to="/classrooms"
            className="px-4 py-2 bg-slate-100/80 dark:bg-slate-800/40 hover:bg-slate-100 dark:hover:bg-slate-800/80 transition-colors rounded-lg text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:text-slate-50"
          >
            Annuler
          </Link>
          <button
            type="submit"
            className="px-4 py-2 bg-forma-600 hover:bg-forma-500 transition-colors rounded-lg text-white"
          >
            {isEditing ? "Mettre à jour" : "Ajouter"}
          </button>
        </div>
      </form>
    </div>
  );
};

const ClassroomsPage = () => {
  const location = useLocation();
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [classroomsPerPage] = useState(10);
  const [availabilityFilter, setAvailabilityFilter] = useState('all');

  const [classrooms, setClassrooms] = useState([]);
  
  // Fonction pour rafraîchir les données
  const refreshData = () => {
    try {
      const storedClassrooms = localStorageService.getAllItems('classrooms');
      console.log(`Loaded ${storedClassrooms.length} classrooms from localStorage`);
      setClassrooms(storedClassrooms);
    } catch (error) {
      console.error('Error refreshing classrooms data:', error);
    }
  };

  // Rafraîchir les données lorsque la route change
  useEffect(() => {
    refreshData();
  }, [location]);

  // Charger les données depuis localStorage
  useEffect(() => {
    const storedClassrooms = localStorageService.getAllItems('classrooms');
    
    // No mock classrooms - using only real classroom data
    setClassrooms(storedClassrooms);
  }, []);

  // Récupérer tous les types de salles uniques
  const classroomTypes = [...new Set(classrooms.map(classroom => classroom.type || '').filter(Boolean))];

  const allClassrooms = classrooms;
  const filteredClassrooms = allClassrooms.filter(classroom => {
    const matchesSearch = (classroom.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                          (classroom.code || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                          (classroom.type || '').toLowerCase().includes(searchTerm.toLowerCase());
    
    if (!matchesSearch) return false;
    
    if (typeFilter !== 'all' && classroom.type !== typeFilter) return false;
    
    if (availabilityFilter === 'available' && !classroom.isAvailable) return false;
    if (availabilityFilter === 'occupied' && classroom.isAvailable) return false;
    
    return true;
  });
  
  const paginatedClassrooms = filteredClassrooms.slice((currentPage - 1) * classroomsPerPage, currentPage * classroomsPerPage);
  const totalClassrooms = filteredClassrooms.length;
  const totalPages = Math.ceil(totalClassrooms / classroomsPerPage);

  const pageSliceLabel =
    totalClassrooms === 0
      ? '—'
      : `${Math.min((currentPage - 1) * classroomsPerPage + 1, totalClassrooms)}–${Math.min(
          currentPage * classroomsPerPage,
          totalClassrooms
        )}`;

  const pageNav = useMemo(
    () =>
      paginationItems(
        Math.min(Math.max(currentPage, 1), Math.max(totalPages, 1)),
        Math.max(totalPages, 1)
      ),
    [currentPage, totalPages]
  );

  const formaLinkBtn = FORMA_LINK_BTN;

  const statsStrip = [
    {
      title: 'Salles',
      value: classrooms.length.toString(),
      hint: 'Enregistrées',
      icon: BuildingLibraryIcon,
      iconWrap:
        'bg-forma-100 text-forma-700 shadow-inner dark:bg-forma-950/50 dark:text-forma-400',
    },
    {
      title: 'Disponibles',
      value: classrooms.filter((c) => c.isAvailable).length.toString(),
      hint: 'Statut libre',
      icon: CheckCircleIcon,
      iconWrap:
        'bg-emerald-100 text-emerald-800 shadow-inner dark:bg-emerald-950/45 dark:text-emerald-400',
    },
    {
      title: 'Capacité',
      value: `${classrooms.reduce((acc, c) => acc + (Number(c.capacity) || 0), 0)}`,
      hint: 'Places cumulées',
      icon: UsersIcon,
      iconWrap: 'bg-rose-100 text-rose-800 shadow-inner dark:bg-rose-950/40 dark:text-rose-400',
    },
  ];

  const typeTabs = useMemo(
    () => [{ id: 'all', name: 'Tous les types' }, ...classroomTypes.map((t) => ({ id: t, name: t }))],
    [classroomTypes]
  );

  const typeTabCounts = useMemo(() => {
    const base = allClassrooms.filter((c) => {
      if (availabilityFilter === 'available' && !c.isAvailable) return false;
      if (availabilityFilter === 'occupied' && c.isAvailable) return false;
      const q = searchTerm.trim().toLowerCase();
      if (!q) return true;
      return (
        (c.name || '').toLowerCase().includes(q) ||
        (c.code || '').toLowerCase().includes(q) ||
        (c.type || '').toLowerCase().includes(q)
      );
    });
    const counts = { all: base.length };
    classroomTypes.forEach((t) => {
      counts[t] = base.filter((c) => c.type === t).length;
    });
    return counts;
  }, [allClassrooms, searchTerm, availabilityFilter, classroomTypes]);

  const availTabs = [
    { id: 'all', name: 'Tous statuts' },
    { id: 'available', name: 'Disponibles' },
    { id: 'occupied', name: 'Occupées' },
  ];

  const availTabCounts = useMemo(() => {
    const base = allClassrooms.filter((c) => {
      if (typeFilter !== 'all' && c.type !== typeFilter) return false;
      const q = searchTerm.trim().toLowerCase();
      if (!q) return true;
      return (
        (c.name || '').toLowerCase().includes(q) ||
        (c.code || '').toLowerCase().includes(q) ||
        (c.type || '').toLowerCase().includes(q)
      );
    });
    return {
      all: base.length,
      available: base.filter((c) => c.isAvailable).length,
      occupied: base.filter((c) => !c.isAvailable).length,
    };
  }, [allClassrooms, searchTerm, typeFilter]);

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handleTypeFilter = (type) => {
    setTypeFilter(type);
    setCurrentPage(1);
  };
  
  const handleAvailabilityFilter = (availability) => {
    setAvailabilityFilter(availability);
    setCurrentPage(1);
  };

  const handleDelete = async (classroomId) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cette salle ?')) {
      try {
        // Supprimer de localStorage
        localStorageService.deleteItem('classrooms', classroomId);
        
        // Mettre à jour l'état pour refléter les changements
        setClassrooms(prevClassrooms => prevClassrooms.filter(classroom => classroom.id !== classroomId));
      } catch (error) {
        console.error('Erreur lors de la suppression:', error);
        alert('Une erreur est survenue lors de la suppression');
      }
    }
  };

  return (
    <Routes>
      <Route
        path="/"
        element={
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            transition={{ duration: 0.3 }}
            className="space-y-6"
          >
            <PageHeader
              title="Salles"
              description="Gestion des salles et espaces de formation"
              actions={
                <Link to="/classrooms/add" className={formaLinkBtn}>
                  <PlusIcon className="h-5 w-5" />
                  <span>Nouvelle salle</span>
                </Link>
              }
            />

            <UiCard padding={false} className="overflow-hidden">
              <div className="border-b border-slate-200/90 bg-gradient-to-br from-slate-50 via-white to-slate-50/80 dark:border-slate-700 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950">
                <div className="grid gap-6 p-6 sm:grid-cols-3 sm:gap-5">
                  {statsStrip.map((stat, index) => (
                    <motion.div
                      key={stat.title}
                      layout
                      initial={{ opacity: 0, y: 12 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ type: 'spring', stiffness: 380, damping: 28, delay: index * 0.06 }}
                      whileHover={{ y: -3 }}
                      className="group relative rounded-2xl border border-slate-200/80 bg-white/80 p-[1px] shadow-sm backdrop-blur-sm dark:border-slate-700/80 dark:bg-slate-950/55"
                    >
                      <div className="relative overflow-hidden rounded-[0.9375rem] bg-gradient-to-b from-white/90 to-white/35 p-4 dark:from-slate-950/95 dark:to-slate-950/65">
                        <div
                          aria-hidden
                          className="pointer-events-none absolute -right-6 -top-6 h-24 w-24 rounded-full bg-gradient-to-br from-forma-200/35 to-transparent opacity-70 blur-2xl transition group-hover:opacity-100 dark:from-forma-600/25"
                        />
                        <div className="relative flex gap-4">
                          <div
                            className={clsx(
                              'relative flex h-12 w-12 shrink-0 items-center justify-center rounded-xl ring-2 ring-white/70 dark:ring-slate-800/80',
                              stat.iconWrap
                            )}
                          >
                            <stat.icon className="h-6 w-6" />
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                              {stat.title}
                            </p>
                            <p className="mt-0.5 text-3xl font-bold tabular-nums tracking-tight text-slate-900 dark:text-white">
                              {stat.value}
                            </p>
                            <p className="mt-1 border-t border-slate-100 pt-2 text-xs text-slate-500 dark:border-slate-700/70 dark:text-slate-400">
                              {stat.hint}
                            </p>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>

              <div className="border-b border-slate-200 bg-white/40 px-5 py-4 dark:border-slate-700 dark:bg-slate-900/35">
                <div className="flex flex-col gap-4">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                        Type de salle
                      </span>
                    </div>
                    <div className="mt-3 flex flex-wrap gap-2">
                      {typeTabs.map((tab) => (
                        <button
                          key={tab.id}
                          type="button"
                          onClick={() => {
                            handleTypeFilter(tab.id);
                            setCurrentPage(1);
                          }}
                          className={clsx(
                            'inline-flex items-center rounded-full px-3.5 py-2 text-sm font-medium transition-all',
                            typeFilter === tab.id
                              ? 'scale-[1.02] bg-forma-600 text-white shadow-md shadow-forma-600/25'
                              : 'bg-slate-100 text-slate-700 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700'
                          )}
                        >
                          <span className="max-w-[12rem] truncate">{tab.name}</span>
                          <span
                            className={clsx(
                              'ml-2 min-w-[1.75rem] rounded-full px-2 py-0.5 text-center text-xs font-semibold tabular-nums',
                              typeFilter === tab.id
                                ? 'bg-white/20 text-white'
                                : 'bg-white/70 text-slate-800 dark:bg-slate-950 dark:text-slate-200'
                            )}
                          >
                            {typeTabCounts[tab.id] ?? 0}
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                        Disponibilité
                      </span>
                    </div>
                    <div className="mt-3 flex flex-wrap gap-2">
                      {availTabs.map((tab) => (
                        <button
                          key={tab.id}
                          type="button"
                          onClick={() => {
                            handleAvailabilityFilter(tab.id);
                            setCurrentPage(1);
                          }}
                          className={clsx(
                            'inline-flex items-center rounded-full px-3.5 py-2 text-sm font-medium transition-all',
                            availabilityFilter === tab.id
                              ? 'scale-[1.02] bg-forma-600 text-white shadow-md shadow-forma-600/25'
                              : 'bg-slate-100 text-slate-700 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700'
                          )}
                        >
                          <span>{tab.name}</span>
                          <span
                            className={clsx(
                              'ml-2 min-w-[1.75rem] rounded-full px-2 py-0.5 text-center text-xs font-semibold tabular-nums',
                              availabilityFilter === tab.id
                                ? 'bg-white/20 text-white'
                                : 'bg-white/70 text-slate-800 dark:bg-slate-950 dark:text-slate-200'
                            )}
                          >
                            {availTabCounts[tab.id] ?? 0}
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="relative w-full lg:max-w-md">
                    <label htmlFor="classroom-search" className="sr-only">
                      Rechercher une salle
                    </label>
                    <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" />
                    <input
                      id="classroom-search"
                      type="search"
                      autoComplete="off"
                      placeholder="Nom, code ou type…"
                      value={searchTerm}
                      onChange={handleSearch}
                      className={clsx(
                        'w-full rounded-xl border border-slate-200 bg-white py-2.5 pl-10 text-sm text-slate-900 shadow-sm placeholder:text-slate-400 focus:border-forma-500 focus:outline-none focus:ring-2 focus:ring-forma-500/25 dark:border-slate-600 dark:bg-slate-950 dark:text-slate-100 dark:placeholder:text-slate-500',
                        searchTerm.trim() ? 'pr-10' : 'pr-4'
                      )}
                    />
                    {searchTerm.trim() !== '' ? (
                      <button
                        type="button"
                        aria-label="Effacer la recherche"
                        onClick={() => {
                          setSearchTerm('');
                          setCurrentPage(1);
                        }}
                        className="absolute right-2 top-1/2 -translate-y-1/2 rounded-lg p-1 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-slate-800 dark:hover:text-slate-200"
                      >
                        <XMarkIcon className="h-5 w-5" />
                      </button>
                    ) : null}
                  </div>
                </div>
              </div>

              {(searchTerm.trim() !== '' || typeFilter !== 'all' || availabilityFilter !== 'all') && (
                <div className="flex flex-wrap items-center gap-2 border-b border-forma-200/60 bg-forma-50/50 px-5 py-2.5 dark:border-forma-900/35 dark:bg-forma-950/25">
                  <span className="text-[11px] font-bold uppercase tracking-wider text-forma-800 dark:text-forma-400">
                    Filtres actifs
                  </span>
                  {searchTerm.trim() !== '' ? (
                    <span className="inline-flex items-center gap-1 rounded-full border border-forma-200/70 bg-white px-2.5 py-1 text-xs font-medium text-slate-700 shadow-sm dark:border-forma-900/60 dark:bg-slate-950 dark:text-slate-200">
                      « {searchTerm.trim()} »
                    </span>
                  ) : null}
                  {typeFilter !== 'all' ? (
                    <span className="inline-flex items-center gap-1 rounded-full border border-forma-200/70 bg-white px-2.5 py-1 text-xs font-medium text-slate-700 shadow-sm dark:border-forma-900/60 dark:bg-slate-950 dark:text-slate-200">
                      {typeFilter}
                    </span>
                  ) : null}
                  {availabilityFilter !== 'all' ? (
                    <span className="inline-flex items-center gap-1 rounded-full border border-forma-200/70 bg-white px-2.5 py-1 text-xs font-medium text-slate-700 shadow-sm dark:border-forma-900/60 dark:bg-slate-950 dark:text-slate-200">
                      {availabilityFilter === 'available' ? 'Disponibles' : 'Occupées'}
                    </span>
                  ) : null}
                  <button
                    type="button"
                    onClick={() => {
                      setSearchTerm('');
                      setTypeFilter('all');
                      setAvailabilityFilter('all');
                      setCurrentPage(1);
                    }}
                    className="ml-auto rounded-lg border border-slate-200 bg-white px-2.5 py-1 text-xs font-semibold text-slate-600 transition hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-300 dark:hover:bg-slate-800"
                  >
                    Réinitialiser
                  </button>
                </div>
              )}

              {paginatedClassrooms.length === 0 ? (
                <div className="flex flex-col items-center justify-center gap-5 px-6 py-16 text-center">
                  <div className="flex h-20 w-20 items-center justify-center rounded-2xl border border-dashed border-slate-200 bg-slate-50 dark:border-slate-700 dark:bg-slate-800/60">
                    <BuildingLibraryIcon className="h-10 w-10 text-forma-500/70 dark:text-forma-400/80" />
                  </div>
                  <div className="max-w-md space-y-2">
                    <h3 className="text-lg font-semibold text-slate-900 dark:text-white">Aucune salle à afficher</h3>
                    <p className="text-sm text-slate-600 dark:text-slate-400">
                      Modifiez les filtres ou ajoutez une salle.
                    </p>
                    <button
                      type="button"
                      onClick={() => {
                        setSearchTerm('');
                        setTypeFilter('all');
                        setAvailabilityFilter('all');
                        setCurrentPage(1);
                      }}
                      className="mx-auto rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
                    >
                      Effacer filtres et recherche
                    </button>
                  </div>
                  <Link to="/classrooms/add" className={formaLinkBtn}>
                    <PlusIcon className="h-5 w-5" />
                    <span>Ajouter une salle</span>
                  </Link>
                </div>
              ) : (
                <>
                  <div className="border-b border-slate-200 bg-gradient-to-r from-white to-slate-50/95 px-6 py-4 dark:border-slate-700 dark:from-slate-900 dark:to-slate-950/90">
                    <h2 className="text-lg font-semibold tracking-tight text-slate-900 dark:text-white">Liste des salles</h2>
                    <p className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-slate-600 dark:text-slate-400">
                      <span>
                        <span className="font-semibold tabular-nums text-slate-900 dark:text-slate-100">
                          {totalClassrooms}
                        </span>{' '}
                        résultat{totalClassrooms > 1 ? 's' : ''}
                      </span>
                      <span className="text-slate-300 dark:text-slate-600" aria-hidden>
                        ·
                      </span>
                      <span>
                        plage{' '}
                        <span className="font-medium tabular-nums text-slate-800 dark:text-slate-200">
                          {pageSliceLabel}
                        </span>{' '}
                        sur <span className="font-medium tabular-nums">{totalClassrooms}</span>
                      </span>
                      <span className="text-slate-300 dark:text-slate-600" aria-hidden>
                        ·
                      </span>
                      <span className="text-slate-500">{classroomsPerPage} par page</span>
                    </p>
                  </div>

                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[720px] border-collapse text-left">
                      <thead className="sticky top-0 z-10 border-b border-slate-200 bg-white/95 text-[11px] font-bold uppercase tracking-wider text-slate-500 shadow-[0_1px_0_rgba(15,23,42,0.06)] backdrop-blur-md dark:border-slate-700 dark:bg-slate-900/95 dark:text-slate-400">
                        <tr>
                          <th scope="col" className="px-4 py-3.5 font-bold">
                            Salle
                          </th>
                          <th scope="col" className="px-4 py-3.5 font-bold">
                            Type
                          </th>
                          <th scope="col" className="px-4 py-3.5 font-bold">
                            Capacité
                          </th>
                          <th scope="col" className="px-4 py-3.5 font-bold">
                            Étage
                          </th>
                          <th scope="col" className="px-4 py-3.5 font-bold">
                            Statut
                          </th>
                          <th scope="col" className="px-4 py-3.5 text-right font-bold">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedClassrooms.map((classroom, rowIdx) => (
                          <motion.tr
                            key={classroom.id}
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: Math.min(rowIdx * 0.03, 0.35) }}
                            className={clsx(
                              'border-b border-slate-100 transition-colors dark:border-slate-800/80',
                              rowIdx % 2 === 1 && 'bg-slate-50/45 dark:bg-slate-900/40',
                              'hover:bg-forma-50/50 dark:hover:bg-forma-950/20'
                            )}
                          >
                            <td className="whitespace-nowrap px-4 py-3.5">
                              <div className="flex items-center">
                                <div className="mr-3 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-forma-100 dark:bg-forma-950/50">
                                  <BuildingLibraryIcon className="h-5 w-5 text-forma-600 dark:text-forma-400" />
                                </div>
                                <div>
                                  <div className="text-sm font-medium text-slate-900 dark:text-slate-50">
                                    {classroom.name || 'Sans nom'}
                                  </div>
                                  <div className="text-xs text-slate-600 dark:text-slate-400">
                                    {classroom.code || '—'}
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td className="whitespace-nowrap px-4 py-3.5">
                              <span className="rounded-full bg-forma-100 px-2.5 py-1 text-xs font-medium text-forma-800 dark:bg-forma-950/45 dark:text-forma-400">
                                {classroom.type || '—'}
                              </span>
                            </td>
                            <td className="whitespace-nowrap px-4 py-3.5 text-sm text-slate-700 dark:text-slate-300">
                              {classroom.capacity ?? '0'} places
                            </td>
                            <td className="whitespace-nowrap px-4 py-3.5 text-sm text-slate-700 dark:text-slate-300">
                              {classroom.floor || '—'}
                            </td>
                            <td className="whitespace-nowrap px-4 py-3.5">
                              <span
                                className={clsx(
                                  'rounded-full px-2.5 py-1 text-xs font-medium',
                                  classroom.isAvailable
                                    ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-500/15 dark:text-emerald-400'
                                    : 'bg-rose-100 text-rose-800 dark:bg-rose-500/15 dark:text-rose-400'
                                )}
                              >
                                {classroom.isAvailable ? 'Disponible' : 'Occupée'}
                              </span>
                            </td>
                            <td className="whitespace-nowrap px-4 py-3.5 text-right text-sm">
                              <div className="flex justify-end gap-1.5">
                                <Link
                                  to={`/classrooms/${classroom.id}`}
                                  className="rounded-lg border border-slate-200 bg-white p-2 text-slate-600 shadow-sm transition hover:bg-slate-50 hover:text-forma-600 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-forma-400"
                                  title="Voir"
                                >
                                  <EyeIcon className="h-4 w-4" />
                                </Link>
                                <Link
                                  to={`/classrooms/edit/${classroom.id}`}
                                  className="rounded-lg border border-slate-200 bg-white p-2 text-slate-600 shadow-sm transition hover:bg-slate-50 hover:text-amber-600 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-400 dark:hover:bg-slate-800"
                                  title="Modifier"
                                >
                                  <PencilIcon className="h-4 w-4" />
                                </Link>
                                <button
                                  type="button"
                                  onClick={() => handleDelete(classroom.id)}
                                  className="rounded-lg border border-slate-200 bg-white p-2 text-red-600 shadow-sm transition hover:bg-red-50 dark:border-slate-600 dark:bg-slate-900 dark:text-red-400 dark:hover:bg-red-950/30"
                                  title="Supprimer"
                                >
                                  <TrashIcon className="h-4 w-4" />
                                </button>
                              </div>
                            </td>
                          </motion.tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {totalClassrooms > classroomsPerPage && (
                    <div className="flex flex-col gap-4 border-t border-slate-200 px-6 py-4 dark:border-slate-600 sm:flex-row sm:items-center sm:justify-between">
                      <p className="text-center text-sm text-slate-600 dark:text-slate-400 sm:text-left">
                        Affichage{' '}
                        <span className="font-semibold tabular-nums text-slate-800 dark:text-slate-200">
                          {pageSliceLabel}
                        </span>{' '}
                        sur <span className="font-semibold tabular-nums">{totalClassrooms}</span>
                        {' · '}page <span className="tabular-nums">{currentPage}</span> sur{' '}
                        <span className="tabular-nums">{totalPages}</span>
                      </p>
                      <nav
                        className="flex flex-wrap items-center justify-center gap-1.5 sm:justify-end"
                        aria-label="Pagination"
                      >
                        <button
                          type="button"
                          onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                          disabled={currentPage === 1}
                          className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-sm font-medium text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
                        >
                          <ChevronLeftIcon className="h-4 w-4" aria-hidden />
                          Précédent
                        </button>
                        <div className="mx-1 flex flex-wrap items-center justify-center gap-1">
                          {pageNav.map((item, idx) =>
                            item.type === 'ellipsis' ? (
                              <span
                                key={'e-' + idx}
                                className="px-2 font-medium text-slate-400 dark:text-slate-500"
                                aria-hidden
                              >
                                …
                              </span>
                            ) : (
                              <button
                                key={item.page}
                                type="button"
                                onClick={() => setCurrentPage(item.page)}
                                className={clsx(
                                  'min-w-[2.5rem] rounded-lg px-2 py-1.5 text-sm font-semibold tabular-nums transition',
                                  item.page === currentPage
                                    ? 'bg-forma-600 text-white shadow-sm'
                                    : 'border border-transparent text-slate-700 hover:bg-slate-100 dark:text-slate-200 dark:hover:bg-slate-800'
                                )}
                              >
                                {item.page}
                              </button>
                            )
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                          disabled={currentPage === totalPages}
                          className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-sm font-medium text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
                        >
                          Suivant
                          <ChevronRightIcon className="h-4 w-4" aria-hidden />
                        </button>
                      </nav>
                    </div>
                  )}
                </>
              )}
            </UiCard>

          </motion.div>
        }
      />
      <Route
        path="/add"
        element={
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            transition={{ duration: 0.3 }}
          >
            <div className="mb-6">
              <Link to="/classrooms" className="text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:text-slate-50 flex items-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M9.707 14.707a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 1.414L7.414 9H15a1 1 0 110 2H7.414l2.293 2.293a1 1 0 010 1.414z" clipRule="evenodd" />
                </svg>
                Retour à la liste
              </Link>
            </div>
            <ClassroomForm />
          </motion.div>
        }
      />
      <Route
        path="/edit/:id"
        element={
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            transition={{ duration: 0.3 }}
          >
            <div className="mb-6">
              <Link to="/classrooms" className="text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:text-slate-50 flex items-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M9.707 14.707a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 1.414L7.414 9H15a1 1 0 110 2H7.414l2.293 2.293a1 1 0 010 1.414z" clipRule="evenodd" />
                </svg>
                Retour à la liste
              </Link>
            </div>
            <ClassroomForm 
              isEditing={true} 
              classroomData={classrooms.find(c => c.id === window.location.pathname.split('/').pop())} 
            />
          </motion.div>
        }
      />
      <Route
        path="/:id"
        element={
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            transition={{ duration: 0.3 }}
          >
            <div className="mb-6">
              <Link to="/classrooms" className="text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:text-slate-50 flex items-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M9.707 14.707a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 1.414L7.414 9H15a1 1 0 110 2H7.414l2.293 2.293a1 1 0 010 1.414z" clipRule="evenodd" />
                </svg>
                Retour à la liste
              </Link>
            </div>
            <ClassroomDetail 
              classroom={classrooms.find(c => c.id === window.location.pathname.split('/').pop())} 
            />
          </motion.div>
        }
      />
    </Routes>
  );
};

export default ClassroomsPage; 